import React, { useEffect, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import AdminDateInput from "../AdminDateInput";
import { formatInr } from "../../utils/financeFormat";
import TableTotalAmount, { ANALYTICS_TABLE_TOTAL_ROW_CLASS } from "./TableTotalAmount";

/** Multi-line cash payment entry for a monthly cost module (admin analytics). */
export default function MonthlyCashPaymentPanel({
  slotId,
  label = "Cash paid this month",
  hint,
  lines = [],
  totalRupee = 0,
  saving = false,
  onAddLine,
  onDeleteLine,
}) {
  const [txnDate, setTxnDate] = useState("");
  const [amount, setAmount] = useState("");
  const [person, setPerson] = useState("");
  const [comments, setComments] = useState("");

  useEffect(() => {
    setTxnDate("");
    setAmount("");
    setPerson("");
    setComments("");
  }, [slotId]);

  const handleAdd = () => {
    const rupee = Number(amount) || 0;
    if (rupee <= 0) return;
    onAddLine?.(slotId, {
      txnDateIso: txnDate,
      amountRupee: rupee,
      person: person.trim(),
      comments: comments.trim(),
    });
    setAmount("");
    setPerson("");
    setComments("");
  };

  return (
    <div className="border border-secondary rounded p-3 mb-3 mt-2">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
        <h5 className="h6 text-white mb-0">{label}</h5>
        <div className="text-secondary small">
          Cash total: <strong className="text-warning">{formatInr(totalRupee)}</strong>
        </div>
      </div>
      {hint ? <p className="text-secondary small mb-2">{hint}</p> : null}

      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-2">
          <Form.Label className="text-secondary small">Date</Form.Label>
          <AdminDateInput
            size="sm"
            value={txnDate}
            onChange={(e) => setTxnDate(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <Form.Label className="text-secondary small">Amount ₹</Form.Label>
          <Form.Control
            size="sm"
            type="number"
            min={0}
            className="text-bg-dark border-secondary"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <Form.Label className="text-secondary small">Person</Form.Label>
          <Form.Control
            size="sm"
            type="text"
            className="text-bg-dark border-secondary"
            value={person}
            onChange={(e) => setPerson(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <Form.Label className="text-secondary small">Comments</Form.Label>
          <Form.Control
            size="sm"
            type="text"
            className="text-bg-dark border-secondary"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <Button size="sm" variant="success" disabled={saving || !(Number(amount) > 0)} onClick={handleAdd}>
            {saving ? "Saving…" : "Add cash payment"}
          </Button>
        </div>
      </div>

      <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
        <thead>
          <tr>
            <th>Date</th>
            <th className="text-end">Amount ₹</th>
            <th>Person</th>
            <th>Comments</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-secondary small">
                No cash payments recorded yet.
              </td>
            </tr>
          ) : (
            <>
              {lines.map((line) => (
                <tr key={`cash-line-${slotId}-${line.id}`}>
                  <td>{line.txnDateIso || "—"}</td>
                  <td className="text-end text-warning">{formatInr(line.amountRupee)}</td>
                  <td>{line.person || "—"}</td>
                  <td className="small">{line.comments || "—"}</td>
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant="outline-danger"
                      disabled={saving}
                      onClick={() => onDeleteLine?.(slotId, line.id)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
              <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
                <td colSpan={1}>Total cash</td>
                <td className="text-end fs-6">
                  <TableTotalAmount amount={totalRupee} toneClass="text-warning" />
                </td>
                <td colSpan={3} />
              </tr>
            </>
          )}
        </tbody>
      </Table>
    </div>
  );
}
